/**
 * SidebarStep2 - Event Details (v2 Sidebar Layout)
 * 
 * THE FORK POINT: Calculation Mode selection determines the entire workflow.
 * - Standard BI: Historical BI engine with V-Test causation
 * - Future Forced Closure: 36-month pro forma with Monte Carlo ranges
 * 
 * This component wraps the existing Step2 form logic with the new sidebar layout.
 * EXACT same functionality - only layout changes.
 */

import React, { useState, useEffect } from 'react';
import { AlertCircle, Info, Calendar, Building, Zap, Check, HelpCircle, Target, Clock, FileText, ChevronDown } from 'lucide-react';
import WizardV2Layout from './WizardV2Layout';
import { apiFetch } from '../../../utils/api';
import { useAuth } from '../../../contexts/TestAuthContext';
import { toast } from 'sonner';

interface EventTemplate {
  id: string;
  name: string;
  event_type: string;
  description?: string;
  calculation_mode: string;
  default_closure_months: number;
  continuation_rates: Record<string, number>;
  common_extra_expenses: string[];
  attorney_notes?: string;
  is_active: boolean;
  is_system: boolean;
}

interface SidebarStep2Props {
  goToStep?: (step: number) => void;
  onNext: (data: any) => void;
  onBack: () => void;
  data: any;
  isSaving?: boolean;
  lastSaved?: Date | null;
  onSave?: () => void;
  highestCompletedStep?: number;
  claimId?: string;
  onDataUpdate?: (data: any) => void;
}

export default function SidebarStep2({ onNext, onBack, data, isSaving, lastSaved, onSave, goToStep, highestCompletedStep, claimId, onDataUpdate }: SidebarStep2Props) {
  const { getToken } = useAuth();
  const [formData, setFormData] = useState({
    business_type: data.business_type || '',
    event_type: data.event_type || '',
    event_start_date: data.event_start_date || '',
    event_end_date: data.event_end_date || '',
    fema_declaration_number: data.fema_declaration_number || '',
    fema_notes: data.fema_notes || '',
    calculation_mode: data.calculation_mode || 'standard_bi',
    closure_duration_months: data.closure_duration_months || 36,
    applied_template_id: data.applied_template_id || '',
    continuation_rates: data.continuation_rates || {}
  });
  
  const [showEventInfo, setShowEventInfo] = useState(false);
  const [showModeInfo, setShowModeInfo] = useState<'standard' | 'future' | null>(null);
  const [activeSection, setActiveSection] = useState('calc_mode');
  const [validationError, setValidationError] = useState<string | null>(null);
  
  // Event Templates state
  const [templates, setTemplates] = useState<EventTemplate[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [showTemplateDropdown, setShowTemplateDropdown] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<EventTemplate | null>(null);

  // Fetch event templates on mount
  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const token = await getToken();
        const response = await apiFetch<EventTemplate[]>('/api/v1/event-templates/', {
          headers: { Authorization: `Bearer ${token}` },
          showErrorToast: false
        });
        // Filter to only show active templates
        const activeTemplates = (response || []).filter(t => t.is_active);
        setTemplates(activeTemplates);
        
        // If data has applied_template_id, find and set the selected template
        if (data.applied_template_id) {
          const applied = activeTemplates.find(t => t.id === data.applied_template_id);
          if (applied) {
            setSelectedTemplate(applied);
          }
        }
      } catch (error) {
        console.error('Error fetching event templates:', error);
      } finally {
        setLoadingTemplates(false);
      }
    };
    fetchTemplates();
  }, [getToken, data.applied_template_id]);

  // Apply a template to the form
  const applyTemplate = (template: EventTemplate) => {
    const newFormData = {
      ...formData,
      event_type: template.event_type,
      calculation_mode: template.calculation_mode,
      closure_duration_months: template.default_closure_months,
      continuation_rates: template.continuation_rates || {},
      applied_template_id: template.id
    };
    
    setFormData(newFormData);
    setSelectedTemplate(template);
    setShowTemplateDropdown(false);
    
    // Propagate to parent immediately
    if (onDataUpdate) {
      onDataUpdate(newFormData);
    }
    
    toast.success(`Applied template: ${template.name}`);
  };

  // Clear template selection
  const clearTemplate = () => {
    setSelectedTemplate(null);
    setFormData(prev => ({
      ...prev,
      applied_template_id: ''
    }));
  };

  const businessTypes = [
    'Restaurant', 
    'Retail', 
    'Services', 
    'Manufacturing', 
    'Nonprofit/Charity',
    'Healthcare',
    'Hospitality/Tourism',
    'Construction',
    'Other'
  ];
  
  const eventTypes = [
    {
      value: 'hurricane',
      label: '🌀 Hurricane / Tropical Storm',
      threshold: '20%',
      description: 'NOAA-verified storm damage, flooding, evacuation losses'
    },
    {
      value: 'government_shutdown',
      label: '🏛️ Government Shutdown / Furlough',
      threshold: '15%',
      description: 'Federal contract delays, reduced government spending impact'
    },
    {
      value: 'tariffs',
      label: '📦 Tariffs / Trade War',
      threshold: '18%',
      description: 'Import cost increases, supply chain disruptions (18.6% avg)'
    },
    {
      value: 'wildfire',
      label: '🔥 Wildfire / Fire Damage',
      threshold: '25%',
      description: 'Fire damage, smoke evacuation, tourism decline ($89B 2025)'
    },
    {
      value: 'pandemic',
      label: '😷 Pandemic / Public Health Emergency',
      threshold: '8%',
      description: 'Lockdowns, capacity restrictions (COVID-19 standard)'
    },
    {
      value: 'flood',
      label: '🌊 Flood / Storm Surge',
      threshold: '22%',
      description: 'FEMA-verified flooding, evacuation, infrastructure damage'
    },
    {
      value: 'earthquake',
      label: '🏚️ Earthquake',
      threshold: '30%',
      description: 'USGS-verified seismic damage, utility disruption'
    },
    {
      value: 'civil_unrest',
      label: '⚠️ Civil Unrest / Riots',
      threshold: '20%',
      description: 'Property damage, curfews, reduced foot traffic'
    },
    {
      value: 'cyber_attack',
      label: '💻 Cyber Attack / Ransomware',
      threshold: '15%',
      description: 'System downtime, data breach, operational disruption'
    },
    {
      value: 'toxic_contamination',
      label: '☢️ Toxic Contamination (PFAS/Chemical)',
      threshold: '0%',
      description: 'Forced closure for toxic remediation - requires Future Closure mode',
      suggestFutureClosure: true
    },
    {
      value: 'plant_explosion',
      label: '💥 Plant Explosion / Industrial Accident',
      threshold: '0%',
      description: 'Permanent closure due to industrial disaster',
      suggestFutureClosure: true
    },
    {
      value: 'train_derailment',
      label: '🚂 Train Derailment / Transportation Disaster',
      threshold: '0%',
      description: 'Area contamination from transportation accident',
      suggestFutureClosure: true
    },
    {
      value: 'custom',
      label: '⚙️ Custom Event (User-Defined)',
      threshold: '12%',
      description: 'Flexible parameters for any business disruption'
    }
  ];

  const selectedEvent = eventTypes.find(e => e.value === formData.event_type);
  const isFutureClosureMode = formData.calculation_mode === 'future_closure';

  // Sync formData with parent's data prop
  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      business_type: data.business_type || prev.business_type,
      event_type: data.event_type || prev.event_type,
      event_start_date: data.event_start_date || prev.event_start_date,
      event_end_date: data.event_end_date || prev.event_end_date,
      fema_declaration_number: data.fema_declaration_number || prev.fema_declaration_number,
      fema_notes: data.fema_notes || prev.fema_notes,
      calculation_mode: data.calculation_mode || prev.calculation_mode,
      closure_duration_months: data.closure_duration_months || prev.closure_duration_months
    }));
  }, [data]);

  const handleEventTypeChange = (eventType: string) => {
    const newData = { ...formData, event_type: eventType };

    // Auto-suggest Future Closure for certain event types
    const event = eventTypes.find(e => e.value === eventType);
    if (event?.suggestFutureClosure && formData.calculation_mode === 'standard_bi') {
      // Show suggestion but don't auto-change
    }

    setFormData(newData);
    setValidationError(null);
  };

  const handleSubmit = () => {
    const missingFields = [];

    if (!formData.event_type) missingFields.push('Disruption Event Type');
    if (!formData.business_type) missingFields.push('Business Type');

    if (missingFields.length > 0) {
      setValidationError(`Please complete the following required fields: ${missingFields.join(', ')}`);
      return;
    }
    
    console.log(`✅ Calculation mode: ${formData.calculation_mode}`);
    
    onNext({
      ...formData,
      calculation_mode: formData.calculation_mode
    });
  };

  // Calculate completion
  const requiredComplete = formData.event_type && formData.business_type;
  const completionPercent = Math.round(
    (14 + // Step 1
    (formData.calculation_mode ? 3 : 0) +
    (formData.event_type ? 5 : 0) +
    (formData.business_type ? 5 : 0) +
    (formData.event_start_date && formData.event_end_date ? 1 : 0))
  );

  // Build QA items
  const qaItems = [];
  if (formData.calculation_mode) {
    qaItems.push({ 
      id: 'mode', 
      label: isFutureClosureMode ? 'Future Closure' : 'Standard BI', 
      status: 'ok' as const 
    });
  }
  if (!formData.event_type) qaItems.push({ id: 'event', label: 'Event Type', status: 'missing' as const });
  if (!formData.business_type) qaItems.push({ id: 'business', label: 'Business Type', status: 'missing' as const });

  // Build sections - mode-aware
  const sections = [
    { 
      id: 'template', 
      label: 'Event Template', 
      isActive: activeSection === 'template',
      isComplete: !!selectedTemplate,
      onClick: () => setActiveSection('template')
    },
    { 
      id: 'calc_mode', 
      label: 'Calc Mode', 
      isActive: activeSection === 'calc_mode',
      isComplete: !!formData.calculation_mode,
      onClick: () => setActiveSection('calc_mode')
    },
    { 
      id: 'event', 
      label: 'Event Type', 
      isActive: activeSection === 'event',
      isComplete: !!formData.event_type,
      onClick: () => setActiveSection('event')
    },
    { 
      id: 'business', 
      label: 'Business Type', 
      isActive: activeSection === 'business',
      isComplete: !!formData.business_type,
      onClick: () => setActiveSection('business')
    },
    { 
      id: 'dates', 
      label: 'Event Dates', 
      isActive: activeSection === 'dates',
      isComplete: !!(formData.event_start_date && formData.event_end_date),
      onClick: () => setActiveSection('dates')
    },
  ];

  // Add closure duration for Future Closure mode
  if (isFutureClosureMode) {
    sections.splice(4, 0, {
      id: 'closure_duration',
      label: 'Closure Duration',
      isActive: activeSection === 'closure_duration',
      isComplete: !!formData.closure_duration_months,
      onClick: () => setActiveSection('closure_duration')
    });
  }

  return (
    <WizardV2Layout
      currentStep={2}
      highestCompletedStep={highestCompletedStep}
      stepTitle="Choose Claim Type & Event Details"
      stepSubtitle="Select your calculation mode and event type to configure the claim analysis."
      completionPercent={completionPercent}
      calculationMode={formData.calculation_mode as 'standard_bi' | 'future_closure'}
      sections={sections}
      qaItems={qaItems}
      nextAction={qaItems.find(q => q.status === 'missing') ? {
        label: `Select ${qaItems.find(q => q.status === 'missing')?.label}`,
        description: 'Required to continue'
      } : undefined}
      isSaving={isSaving}
      lastSaved={lastSaved}
      onSave={onSave}
      goToStep={goToStep}
      onBack={onBack}
      onNext={handleSubmit}
      claimId={claimId}
      nextLabel="Continue to Financial Data"
      isNextDisabled={!requiredComplete}
    >
      <div className="p-6 lg:p-8">

        {validationError && (
          <p className="text-red-500 text-sm mb-4">{validationError}</p>
        )}

        {/* ═══════════════════════════════════════════════════════════════════
            EVENT TEMPLATE SELECTOR - Quick setup from pre-configured templates
        ═══════════════════════════════════════════════════════════════════ */}
        {templates.length > 0 && (
          <div className="mb-8">
            <div className="flex items-center gap-2 mb-4">
              <FileText className="w-5 h-5 text-purple-600" />
              <h2 className="text-lg font-bold text-gray-900">Quick Setup with Event Template</h2>
              <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">Optional</span>
            </div>
            
            <div className="bg-gradient-to-r from-purple-50 via-indigo-50 to-purple-50 rounded-xl p-4 border-2 border-purple-200">
              <p className="text-sm text-purple-800 mb-3">
                Select a pre-configured template to auto-fill event type, calculation mode, and default continuation rates.
              </p>
              
              {/* Template Selector Dropdown */}
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setShowTemplateDropdown(!showTemplateDropdown)}
                  className={`w-full flex items-center justify-between px-4 py-3 bg-white border-2 rounded-lg hover:border-purple-400 transition-colors ${
                    selectedTemplate ? 'border-purple-500' : 'border-gray-200'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    {selectedTemplate ? (
                      <>
                        <span className="text-lg">📋</span>
                        <div className="text-left">
                          <p className="font-medium text-gray-900">{selectedTemplate.name}</p>
                          <p className="text-xs text-gray-500">
                            {selectedTemplate.calculation_mode === 'future_closure' ? 'Future Closure' : 'Standard BI'} • {selectedTemplate.default_closure_months} months
                          </p>
                        </div>
                      </>
                    ) : (
                      <>
                        <span className="text-gray-400">Select a template...</span>
                      </>
                    )}
                  </div>
                  <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${showTemplateDropdown ? 'rotate-180' : ''}`} />
                </button>
                
                {/* Dropdown Menu */}
                {showTemplateDropdown && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white border-2 border-purple-200 rounded-lg shadow-lg z-50 max-h-64 overflow-y-auto">
                    {/* Clear Selection Option */}
                    {selectedTemplate && (
                      <button
                        type="button"
                        onClick={() => { clearTemplate(); setShowTemplateDropdown(false); }}
                        className="w-full px-4 py-2 text-left text-sm text-gray-500 hover:bg-gray-50 border-b"
                      >
                        ✕ Clear selection (manual setup)
                      </button>
                    )}
                    
                    {loadingTemplates ? (
                      <div className="px-4 py-3 text-sm text-gray-500">Loading templates...</div>
                    ) : templates.length === 0 ? (
                      <div className="px-4 py-3 text-sm text-gray-500">No templates available</div>
                    ) : (
                      templates.map((template) => (
                        <button
                          key={template.id}
                          type="button"
                          onClick={() => applyTemplate(template)}
                          className={`w-full px-4 py-3 text-left hover:bg-purple-50 transition-colors ${
                            selectedTemplate?.id === template.id ? 'bg-purple-100' : ''
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-medium text-gray-900">{template.name}</p>
                              <p className="text-xs text-gray-500">
                                {template.event_type} • {template.calculation_mode === 'future_closure' ? 'Future Closure' : 'Standard BI'} • {template.default_closure_months}mo
                              </p>
                              {template.description && (
                                <p className="text-xs text-gray-400 mt-1">{template.description}</p>
                              )}
                            </div>
                            {template.is_system && (
                              <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">System</span>
                            )}
                          </div>
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>
              
              {selectedTemplate && (
                <div className="mt-3 p-3 bg-white rounded-lg border border-purple-200">
                  <p className="text-xs font-medium text-purple-700 mb-2">Template applied:</p>
                  <div className="flex flex-wrap gap-2">
                    <span className="text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded">
                      Mode: {selectedTemplate.calculation_mode === 'future_closure' ? 'Future Closure' : 'Standard BI'}
                    </span>
                    <span className="text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded">
                      Duration: {selectedTemplate.default_closure_months} months
                    </span>
                    {selectedTemplate.continuation_rates && Object.keys(selectedTemplate.continuation_rates).length > 0 && (
                      <span className="text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded">
                        {Object.keys(selectedTemplate.continuation_rates).length} expense rates preset
                      </span>
                    )}
                  </div>
                  {selectedTemplate.attorney_notes && (
                    <p className="mt-2 text-xs text-gray-600 italic">"{selectedTemplate.attorney_notes}"</p>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ═══════════════════════════════════════════════════════════════════
            SECTION 0: CALCULATION MODE - THE FORK POINT (TOP OF PAGE)
        ═══════════════════════════════════════════════════════════════════ */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-4">
            <Target className="w-5 h-5 text-blue-600" />
            <h2 className="text-lg font-bold text-gray-900">First, Choose Your Claim Type</h2>
          </div>
          
          <div className="bg-gradient-to-r from-blue-50 via-indigo-50 to-blue-50 rounded-xl p-6 border-2 border-blue-200">
            <p className="text-sm text-blue-800 mb-4">This choice determines your entire workflow and calculation methodology.</p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Standard BI Mode */}
              <label className={`relative flex flex-col p-5 border-2 rounded-xl cursor-pointer hover:shadow-md transition-all ${
                formData.calculation_mode === 'standard_bi'
                  ? 'border-blue-600 bg-white shadow-lg ring-2 ring-blue-200'
                  : 'border-gray-200 bg-white/70 hover:border-blue-300'
              }`}>
                <div className="flex items-start gap-3">
                  <input
                    type="radio"
                    name="calculation_mode"
                    value="standard_bi"
                    checked={formData.calculation_mode === 'standard_bi'}
                    onChange={(e) => setFormData({ ...formData, calculation_mode: e.target.value })}
                    className="mt-1 text-blue-600 focus:ring-blue-500 w-5 h-5"
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-lg">📊</span>
                      <p className="font-bold text-gray-900">Standard BI Calculator</p>
                    </div>
                    <p className="text-sm text-gray-600">
                      For past interruptions already occurred
                    </p>
                    <div className="mt-3 space-y-1">
                      <p className="text-xs text-gray-500 flex items-center gap-1">
                        <Check className="w-3 h-3 text-green-500" /> Requires: 4 years financial data
                      </p>
                      <p className="text-xs text-gray-500 flex items-center gap-1">
                        <Check className="w-3 h-3 text-green-500" /> BI engine with V-Test causation
                      </p>
                      <p className="text-xs text-gray-500 flex items-center gap-1">
                        <Check className="w-3 h-3 text-green-500" /> RTP multipliers applied
                      </p>
                    </div>
                  </div>
                </div>
                
                {/* Tooltip button */}
                <button
                  type="button"
                  onClick={(e) => { e.preventDefault(); setShowModeInfo(showModeInfo === 'standard' ? null : 'standard'); }}
                  className="absolute top-3 right-3 text-gray-400 hover:text-blue-600"
                >
                  <HelpCircle className="w-4 h-4" />
                </button>
                
                {formData.calculation_mode === 'standard_bi' && (
                  <div className="mt-3 pt-3 border-t border-blue-100">
                    <p className="text-xs font-medium text-blue-600">✓ Most common for disaster claims</p>
                  </div>
                )}
              </label>

              {/* Future Closure Mode */}
              <label className={`relative flex flex-col p-5 border-2 rounded-xl cursor-pointer hover:shadow-md transition-all ${
                formData.calculation_mode === 'future_closure'
                  ? 'border-blue-600 bg-white shadow-lg ring-2 ring-blue-200'
                  : 'border-gray-200 bg-white/70 hover:border-blue-300'
              }`}>
                <div className="flex items-start gap-3">
                  <input
                    type="radio"
                    name="calculation_mode"
                    value="future_closure"
                    checked={formData.calculation_mode === 'future_closure'}
                    onChange={(e) => setFormData({ ...formData, calculation_mode: e.target.value })}
                    className="mt-1 text-blue-600 focus:ring-blue-500 w-5 h-5"
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-lg">☢️</span>
                      <p className="font-bold text-gray-900">Future Forced Closure</p>
                    </div>
                    <p className="text-sm text-gray-600">
                      For permanent or long-term closures (e.g., plant explosion, train derailment, PFAS)
                    </p>
                    <div className="mt-3 space-y-1">
                      <p className="text-xs text-gray-500 flex items-center gap-1">
                        <Check className="w-3 h-3 text-blue-500" /> Requires: 36+ months historical data
                      </p>
                      <p className="text-xs text-gray-500 flex items-center gap-1">
                        <Check className="w-3 h-3 text-blue-500" /> Monte Carlo probability ranges
                      </p>
                      <p className="text-xs text-gray-500 flex items-center gap-1">
                        <Check className="w-3 h-3 text-blue-500" /> Pro forma 36-month projection
                      </p>
                    </div>
                  </div>
                </div>
                
                {/* Tooltip button */}
                <button
                  type="button"
                  onClick={(e) => { e.preventDefault(); setShowModeInfo(showModeInfo === 'future' ? null : 'future'); }}
                  className="absolute top-3 right-3 text-gray-400 hover:text-blue-600"
                >
                  <HelpCircle className="w-4 h-4" />
                </button>
                
                {formData.calculation_mode === 'future_closure' && (
                  <div className="mt-3 pt-3 border-t border-blue-100">
                    <p className="text-xs font-medium text-blue-600">⚠️ For 2-4 year closure scenarios</p>
                  </div>
                )}
              </label>
            </div>

            {/* Info Tooltips */}
            {showModeInfo === 'standard' && (
              <div className="mt-4 p-4 bg-blue-100 border border-blue-300 rounded-xl">
                <div className="flex items-start gap-2">
                  <Info className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                  <div className="text-sm text-blue-900">
                    <p className="font-semibold mb-1">Standard BI Calculator Details</p>
                    <ul className="space-y-1 text-xs">
                      <li>• Uses BI (Historical Period Comparability Check) engine</li>
                      <li>• V-Test causation validates revenue decline matches event period</li>
                      <li>• RTP (Return to Pre-event) multipliers project recovery</li>
                      <li>• Best for: Hurricanes, floods, temporary shutdowns, supply chain issues</li>
                    </ul>
                  </div>
                </div>
              </div>
            )}
            
            {showModeInfo === 'future' && (
              <div className="mt-4 p-4 bg-blue-100 border border-blue-300 rounded-xl">
                <div className="flex items-start gap-2">
                  <Info className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                  <div className="text-sm text-blue-900">
                    <p className="font-semibold mb-1">Future Forced Closure Details</p>
                    <ul className="space-y-1 text-xs">
                      <li>• Projects lost profits over 36 months with confidence intervals</li>
                      <li>• Monte Carlo simulation provides low/mid/high demand ranges</li>
                      <li>• Includes shutdown costs, restart costs, continuing expenses</li>
                      <li>• Best for: Toxic contamination, plant explosions, permanent closures</li>
                    </ul>
                  </div>
                </div>
              </div>
            )}

            {/* Closure Duration - Only for Future Closure */}
            {isFutureClosureMode && (
              <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-xl">
                <div className="flex items-center gap-3 mb-3">
                  <Clock className="w-5 h-5 text-blue-600" />
                  <div>
                    <p className="font-semibold text-blue-900">Projected Closure Duration</p>
                    <p className="text-xs text-blue-700">How long will the business remain closed?</p>
                  </div>
                </div>
                <select
                  value={formData.closure_duration_months}
                  onChange={(e) => {
                    const newValue = parseInt(e.target.value);
                    setFormData({ ...formData, closure_duration_months: newValue });
                    // Save immediately to persist the change
                    if (onDataUpdate) {
                      onDataUpdate({ closure_duration_months: newValue });
                    }
                  }}
                  className="w-full md:w-64 px-4 py-2.5 border-2 border-blue-300 rounded-xl focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500 focus:outline-none font-medium text-gray-800 bg-white"
                >
                  <option value={12}>12 months (1 year)</option>
                  <option value={24}>24 months (2 years)</option>
                  <option value={36}>36 months (3 years)</option>
                  <option value={48}>48 months (4 years)</option>
                  <option value={60}>60 months (5 years)</option>
                </select>
              </div>
            )}
          </div>
        </div>
        
        {/* Two-column layout for remaining fields */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
          
          {/* LEFT COLUMN */}
          <div className="space-y-6">
            {/* ═══════════════════════════════════════════════════════════════════
                SECTION 1: Event Type
            ═══════════════════════════════════════════════════════════════════ */}
            <div className="bg-gray-50/50 rounded-xl p-6 border border-gray-100">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                    <Zap className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-base font-semibold text-gray-900">Disruption Event Type <span className="text-red-500">*</span></h3>
                    <p className="text-sm text-gray-500">What caused the business interruption?</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowEventInfo(!showEventInfo)}
                  className="text-blue-600 hover:text-blue-700 text-sm font-medium flex items-center gap-1"
                >
                  <Info className="w-4 h-4" />
                  {showEventInfo ? 'Hide' : 'Info'}
                </button>
              </div>
              
              <select
                value={formData.event_type}
                onChange={(e) => handleEventTypeChange(e.target.value)}
                className={`w-full px-4 py-3 border-2 rounded-xl focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 focus:outline-none font-medium text-gray-800 bg-white transition-all text-sm ${
                  formData.event_type ? 'border-emerald-400' : 'border-gray-200'
                }`}
              >
                <option value="">-- Select Disruption Event --</option>
                {eventTypes.map((event) => (
                  <option key={event.value} value={event.value}>
                    {event.label} {event.threshold !== '0%' ? `(Threshold: ${event.threshold})` : ''}
                  </option>
                ))}
              </select>
              
              {/* Event Description */}
              {selectedEvent && (
                <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-xl">
                  <p className="text-sm text-blue-800 font-semibold">{selectedEvent.label}</p>
                  <p className="text-xs text-blue-700 mt-1">{selectedEvent.description}</p>
                  {selectedEvent.threshold !== '0%' && (
                    <p className="text-xs text-blue-600 mt-2">
                      <strong>Threshold:</strong> {selectedEvent.threshold} revenue decline required
                    </p>
                  )}
                </div>
              )}

              {/* Auto-suggest for events that need Future Closure */}
              {selectedEvent?.suggestFutureClosure && formData.calculation_mode === 'standard_bi' && (
                <div className="mt-4 p-3 bg-orange-100 border border-orange-300 rounded-xl">
                  <div className="flex items-start space-x-2">
                    <AlertCircle className="w-4 h-4 text-orange-600 mt-0.5 flex-shrink-0" />
                    <div className="text-xs text-orange-800">
                      <p className="font-semibold">💡 Suggested: Future Closure Mode</p>
                      <p className="mt-0.5">This event type typically requires long-term closure projections.</p>
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, calculation_mode: 'future_closure' })}
                        className="mt-2 text-orange-700 underline hover:text-orange-900 font-medium"
                      >
                        Switch to Future Closure →
                      </button>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Info Panel */}
              {showEventInfo && (
                <div className="mt-4 p-4 bg-yellow-50 border-l-4 border-yellow-400 rounded-lg">
                  <div className="flex items-start">
                    <AlertCircle className="w-5 h-5 text-yellow-600 mr-2 flex-shrink-0 mt-0.5" />
                    <div className="text-xs text-yellow-800">
                      <p className="font-semibold mb-1">Event-Specific Causation Tests:</p>
                      <ul className="space-y-0.5 ml-2">
                        <li>• Industry-standard thresholds (NOAA, USGS, FEMA)</li>
                        <li>• Higher thresholds for acute events</li>
                        <li>• Recovery lag multipliers applied</li>
                      </ul>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* ═══════════════════════════════════════════════════════════════════
                SECTION 2: Business Type
            ═══════════════════════════════════════════════════════════════════ */}
            <div className="bg-gray-50/50 rounded-xl p-6 border border-gray-100">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                  <Building className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-gray-900">
                    Business Type <span className="text-red-500">*</span>
                  </h3>
                  <p className="text-sm text-gray-500">Select category</p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-2">
                {businessTypes.map((type) => (
                  <button
                    key={type}
                    onClick={() => { setFormData({ ...formData, business_type: type }); setValidationError(null); }}
                    className={`py-2.5 px-3 rounded-lg border-2 font-medium transition-all text-left text-sm ${
                      formData.business_type === type
                        ? 'border-blue-600 bg-blue-50 text-blue-700'
                        : 'border-gray-200 hover:border-blue-300 text-gray-700 bg-white'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="truncate">{type}</span>
                      {formData.business_type === type && (
                        <Check className="w-4 h-4 text-blue-600 flex-shrink-0" />
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </div>

          </div>

          {/* RIGHT COLUMN */}
          <div className="space-y-6">
            {/* ═══════════════════════════════════════════════════════════════════
                SECTION 3: Event Coverage Period (Optional)
            ═══════════════════════════════════════════════════════════════════ */}
            <div className="bg-gray-50/50 rounded-xl p-6 border border-gray-100">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                  <Calendar className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-gray-900">Event Coverage Period</h3>
                  <p className="text-sm text-gray-500">Optional - dates of the disruption</p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
                  <input
                    type="date"
                    value={formData.event_start_date}
                    onChange={(e) => setFormData({ ...formData, event_start_date: e.target.value })}
                    className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 focus:outline-none text-sm bg-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
                  <input
                    type="date"
                    value={formData.event_end_date}
                    onChange={(e) => setFormData({ ...formData, event_end_date: e.target.value })}
                    className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 focus:outline-none text-sm bg-white"
                  />
                </div>
              </div>
              
              {formData.event_start_date && formData.event_end_date && (
                <p className="mt-3 text-sm text-emerald-700 flex items-center gap-2">
                  <Check className="w-4 h-4" />
                  {formData.event_start_date} to {formData.event_end_date}
                </p>
              )}
            </div>

            {/* Mode-specific preview */}
            <div className={`rounded-xl p-6 border-2 ${
              isFutureClosureMode 
                ? 'bg-blue-50 border-blue-200' 
                : 'bg-blue-50 border-blue-200'
            }`}>
              <div className="flex items-center gap-3 mb-4">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                  isFutureClosureMode ? 'bg-blue-100' : 'bg-blue-100'
                }`}>
                  <span className="text-lg">{isFutureClosureMode ? '☢️' : '📊'}</span>
                </div>
                <div>
                  <h3 className={`text-base font-semibold ${isFutureClosureMode ? 'text-blue-900' : 'text-blue-900'}`}>
                    {isFutureClosureMode ? 'Future Closure Path' : 'Standard BI Path'}
                  </h3>
                  <p className={`text-sm ${isFutureClosureMode ? 'text-blue-700' : 'text-blue-700'}`}>
                    What comes next
                  </p>
                </div>
              </div>
              
              <div className={`space-y-2 text-sm ${isFutureClosureMode ? 'text-blue-800' : 'text-blue-800'}`}>
                {isFutureClosureMode ? (
                  <>
                    <p>→ Step 3: Upload Financial Data</p>
                    <p>→ Step 4: Aggression, Base Period, Duration</p>
                    <p>→ Step 5: Expense Continuation & One-Time Costs</p>
                    <p>→ Step 6: Pro Forma Review + Monte Carlo</p>
                    <p>→ Step 7: Future Closure Report</p>
                  </>
                ) : (
                  <>
                    <p>→ Step 3: Upload Financial Data</p>
                    <p>→ Step 4: Payroll & Additional Inputs</p>
                    <p>→ Step 5: BI Review + Causation Test</p>
                    <p>→ Step 6: Final BI Calculation</p>
                    <p>→ Step 7: Standard BI Report</p>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

      </div>
    </WizardV2Layout>
  );
}
